// ===========================================
// RECONNECTING MODAL
// ===========================================
// Overlay shown when WebSocket connection is lost
// Keeps trying to reconnect - seat is reserved on server
// ===========================================

import { useEffect, useState, useRef } from 'react';
import { useSocketStore } from '../stores/socketStore';
import { useHealthCheck } from '../hooks/useHealthCheck';
import { Modal } from './Modal';

interface ReconnectingModalProps {
  /** Table ID for rejoining after reconnect */
  tableId?: string;
  /** Mode (play/spectate) for rejoining */
  mode?: string;
  /** Callback when reconnection succeeds and state is restored */
  onReconnected?: () => void;
  /** Whether to show the modal (can be controlled externally) */
  forceShow?: boolean;
}

// Animated dots component - cycles through 0, 1, 2, 3 dots
function AnimatedDots() {
  const [dotCount, setDotCount] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev + 1) % 4);
    }, 400);
    return () => clearInterval(interval);
  }, []);

  // Use fixed-width spans to prevent text jumping
  return (
    <span className="inline-block w-[24px] text-left">
      {'.'.repeat(dotCount)}
    </span>
  );
}

export function ReconnectingModal({
  tableId,
  mode = 'play',
  onReconnected,
  forceShow = false,
}: ReconnectingModalProps) {
  const { isConnected, isReconnecting, reconnectAttempt, wasKickedFromOtherLocation, clearKickedFlag } = useSocketStore();

  const [showModal, setShowModal] = useState(false);
  const [secondsDisconnected, setSecondsDisconnected] = useState(0);

  const disconnectTimeRef = useRef<number | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const wasConnectedRef = useRef(isConnected);

  // Health check polling - helps reconnect when socket.io's built-in reconnect fails
  useHealthCheck({
    enabled: showModal && !isConnected,
    onHealthy: () => console.log('[ReconnectingModal] Health check passed, socket should reconnect'),
  });

  // Detect disconnect and show modal
  useEffect(() => {
    console.log('[ReconnectingModal] Connection state changed:', {
      wasConnected: wasConnectedRef.current,
      isConnected,
      showModal,
    });

    if (wasConnectedRef.current && !isConnected) {
      // Just disconnected
      console.log('[ReconnectingModal] >>> DISCONNECTED - showing modal');
      disconnectTimeRef.current = Date.now();
      setShowModal(true);
      setSecondsDisconnected(0);
    } else if (!wasConnectedRef.current && isConnected) {
      // Just reconnected
      console.log('[ReconnectingModal] >>> RECONNECTED - hiding modal');
      setShowModal(false);
      disconnectTimeRef.current = null;
      setSecondsDisconnected(0);
      onReconnected?.();
    }
    wasConnectedRef.current = isConnected;
  }, [isConnected, onReconnected]);

  // Force show from parent
  useEffect(() => {
    if (forceShow && !showModal) {
      setShowModal(true);
      disconnectTimeRef.current = Date.now();
      setSecondsDisconnected(0);
    }
  }, [forceShow, showModal]);

  // Timer to track how long disconnected (for display only)
  useEffect(() => {
    if (!showModal || isConnected) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    timerRef.current = setInterval(() => {
      if (disconnectTimeRef.current) {
        const elapsed = Math.floor((Date.now() - disconnectTimeRef.current) / 1000);
        setSecondsDisconnected(elapsed);
      }
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [showModal, isConnected]);

  // Handle refresh button click
  const handleRefresh = () => {
    clearKickedFlag();
    window.location.reload();
  };

  // Show modal if kicked from another location OR if normal disconnect
  if (!showModal && !wasKickedFromOtherLocation) {
    return null;
  }

  // Format time as M:SS
  const minutes = Math.floor(secondsDisconnected / 60);
  const seconds = secondsDisconnected % 60;
  const timeDisplay = minutes > 0
    ? `${minutes}:${seconds.toString().padStart(2, '0')}`
    : `${seconds}s`;

  // Different UI for kicked from another location vs normal reconnecting
  if (wasKickedFromOtherLocation) {
    return (
      <Modal
        variant="alert"
        blocking={true}
        showClose={false}
        closeOnOverlayClick={false}
        closeOnEsc={false}
        maxWidthClass="max-w-sm"
      >
        <div className="p-6 text-center">
          <h2 className="text-2xl font-bold text-casino-gold-400 mb-3">
            CONNECTED ELSEWHERE
          </h2>

          <p className="text-gray-300 text-sm mb-2">
            Your session was moved to another browser tab.
          </p>

          <p className="text-gray-400 text-xs mb-4">
            Only one active session per account is allowed.
          </p>

          <button
            onClick={handleRefresh}
            className="bg-casino-gold-500 hover:bg-casino-gold-600 text-black font-bold py-2 px-6 rounded-lg transition-colors"
          >
            Refresh to Reconnect
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      variant="alert"
      blocking={true}
      showClose={false}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      maxWidthClass="max-w-sm"
    >
      {/* Blocking overlay - prevents gameplay while disconnected */}
      {/* Full table state will be received from server upon reconnection */}
      <div className="p-6 text-center">
        <h2 className="text-2xl font-bold text-casino-gold-400 mb-1">
          RECONNECTING<AnimatedDots />
        </h2>

        <p className="text-gray-300 text-sm mb-2">
          Your connection has been lost.
        </p>

        <p className="text-yellow-400 text-xs mb-4">
          Your seat is reserved for 3 hands.
        </p>

        {/* Status */}
        <p className="text-gray-400 text-xs">
          {isReconnecting && reconnectAttempt > 0 ? (
            <>Attempt {reconnectAttempt} &bull; Disconnected {timeDisplay}</>
          ) : (
            <>Connecting &bull; Disconnected {timeDisplay}</>
          )}
        </p>
      </div>
    </Modal>
  );
}

export default ReconnectingModal;
